import {QueryClient, UseQueryResult, useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import IWBrokerClient from '../client/IWBrokerClient';
import {gatewayRealtimeQueryOptions} from './gatewayQueryUtils';

export type Blind = Record<string, any> & {mac: string};
export type Blinds = Record<string, Blind>;

export const blindsQueryKey = ['blind list'] as const;

const installedBlindsListeners = new WeakSet<QueryClient>();

function installBlindsListener(queryClient: QueryClient) {
    if (installedBlindsListeners.has(queryClient)) {
        return;
    }

    IWBrokerClient.socket.on('blind update', (blind: Blind) => {
        console.log('(blindsQuery) blind update', blind);
        queryClient.setQueryData<Blinds>(blindsQueryKey, (current: Blinds | undefined) => ({
            ...current,
            [blind.mac]: blind
        }));
    });

    installedBlindsListeners.add(queryClient);
}

export function useBlindsQuery(): UseQueryResult<Blinds, Error> {
    const queryClient = useQueryClient();
    installBlindsListener(queryClient);

    return useQuery<Blinds>({
        queryKey: blindsQueryKey,
        queryFn: async () => IWBrokerClient.request('blind list') as Promise<Blinds>,
        ...gatewayRealtimeQueryOptions
    });
}

interface SetBlindPositionVariables {
    mac: string;
    position: number;
}

export function useSetBlindPositionMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: ['blind position'],
        mutationFn: async ({mac, position}: SetBlindPositionVariables) => {
            await IWBrokerClient.request('blind position', {mac, position});
        },
        onMutate: async ({mac, position}) => {
            await queryClient.cancelQueries({queryKey: blindsQueryKey});
            const previousBlinds = queryClient.getQueryData<Blinds>(blindsQueryKey);

            queryClient.setQueryData<Blinds>(blindsQueryKey, (current: Blinds | undefined) => {
                if (!current?.[mac]) {
                    return current;
                }

                return {
                    ...current,
                    [mac]: {
                        ...current[mac],
                        data: {
                            ...current[mac].data,
                            currentPosition: position
                        }
                    }
                };
            });

            return {previousBlinds};
        },
        onError: (_error, _variables, context) => {
            if (context?.previousBlinds) {
                queryClient.setQueryData(blindsQueryKey, context.previousBlinds);
            }
        }
    });
}

export function useSetAllBlindsPositionMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: ['blind all position'],
        mutationFn: async (position: number) => {
            await IWBrokerClient.request('blind all position', {position});
        },
        onMutate: async position => {
            await queryClient.cancelQueries({queryKey: blindsQueryKey});
            const previousBlinds = queryClient.getQueryData<Blinds>(blindsQueryKey);

            queryClient.setQueryData<Blinds>(blindsQueryKey, (current: Blinds | undefined) => {
                if (!current) {
                    return current;
                }

                return Object.fromEntries(Object.entries(current).map(([mac, blind]) => [mac, {
                    ...blind,
                    data: {
                        ...blind.data,
                        currentPosition: position
                    }
                }]));
            });

            return {previousBlinds};
        },
        onError: (_error, _position, context) => {
            if (context?.previousBlinds) {
                queryClient.setQueryData(blindsQueryKey, context.previousBlinds);
            }
        }
    });
}
